import React, { useState, useEffect } from 'react';
import { FileText, Upload, Edit, Trash2, X, Download } from 'lucide-react';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function Documents() {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [editingDoc, setEditingDoc] = useState(null);
  const [editForm, setEditForm] = useState({ nome_file: '', descrizione: '', data_scadenza: '' });
  
  useEffect(() => {
    // Get current user
    const userStr = localStorage.getItem('user');
    if (userStr) {
      setCurrentUser(JSON.parse(userStr));
    }
    fetchDocuments();
  }, []);
  
  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/documents`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setDocuments(data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching documents:', err);
      setLoading(false);
    }
  };

  const canManage = currentUser?.role === 'admin' || currentUser?.role === 'hse_manager';

  const handleDelete = async (docId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo documento?')) return;
    
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/documents/${docId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        setDocuments(documents.filter(d => d._id !== docId));
        alert('Documento eliminato con successo');
      } else {
        const error = await res.json();
        alert(error.detail || 'Errore durante l\'eliminazione');
      }
    } catch (err) {
      console.error('Error deleting document:', err);
      alert('Errore di connessione');
    }
  };

  const openEditModal = (doc) => {
    setEditingDoc(doc);
    setEditForm({
      nome_file: doc.nome_file || '',
      descrizione: doc.descrizione || '',
      data_scadenza: doc.data_scadenza ? new Date(doc.data_scadenza).toISOString().split('T')[0] : ''
    });
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    
    const formData = new FormData();
    formData.append('nome_file', editForm.nome_file);
    formData.append('descrizione', editForm.descrizione);
    formData.append('data_scadenza', editForm.data_scadenza || '');
    
    try {
      const res = await fetch(`${API_URL}/api/documents/${editingDoc._id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      if (res.ok) {
        const updated = await res.json();
        setDocuments(documents.map(d => d._id === updated._id ? updated : d));
        setEditingDoc(null);
        alert('Documento aggiornato con successo');
      } else {
        const error = await res.json();
        alert(error.detail || 'Errore durante l\'aggiornamento');
      }
    } catch (err) {
      console.error('Error updating document:', err);
      alert('Errore di connessione');
    }
  };
  
  if (loading) {
    return (
      <div className="loading" data-testid="loading">
        <p>Caricamento documenti...</p>
      </div>
    );
  }
  
  return (
    <div data-testid="documents-page">
      <div className="page-header">
        <h1 className="page-title">Documenti</h1>
        <p className="page-description">
          Repository centrale documenti
        </p>
      </div>
      
      {documents.length === 0 ? (
        <div className="empty-state">
          <FileText className="empty-state-icon" />
          <p>Nessun documento caricato</p>
        </div>
      ) : (
        <div className="card">
          <div className="table-container">
            <table data-testid="documents-table">
              <thead>
                <tr>
                  <th>Nome File</th>
                  <th>Tipo</th>
                  <th>Entità</th>
                  <th>Scadenza</th>
                  <th>Dimensione</th>
                  <th>Data Caricamento</th>
                  <th>Azioni</th>
                </tr>
              </thead>
              <tbody>
                {documents.map((doc) => (
                  <tr key={doc._id} data-testid="document-row">
                    <td className="font-medium">{doc.nome_file}</td>
                    <td>
                      <span className="badge badge-secondary">
                        {doc.tipo_file?.split('/')[1]?.toUpperCase() || 'FILE'}
                      </span>
                    </td>
                    <td>
                      <span className="badge badge-secondary">{doc.entity_type}</span>
                    </td>
                    <td>
                      {doc.data_scadenza 
                        ? format(new Date(doc.data_scadenza), 'dd/MM/yyyy', { locale: it })
                        : <span className="text-muted-foreground">-</span>
                      }
                    </td>
                    <td>{doc.dimensione ? (doc.dimensione / 1024).toFixed(0) + ' KB' : '-'}</td>
                    <td>
                      {format(new Date(doc.created_at), 'dd/MM/yyyy HH:mm', { locale: it })}
                    </td>
                    <td>
                      <div className="flex gap-1">
                        <a
                          href={`${API_URL}/api/download/${doc._id}`}
                          className="btn btn-secondary btn-sm"
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid="download-document"
                        >
                          <Download size={14} />
                        </a>
                        {canManage && (
                          <>
                            <button
                              className="btn btn-secondary btn-sm"
                              onClick={() => openEditModal(doc)}
                              title="Modifica"
                              data-testid={`edit-doc-${doc._id}`}
                            >
                              <Edit size={14} />
                            </button>
                            <button
                              className="btn btn-secondary btn-sm text-destructive"
                              onClick={() => handleDelete(doc._id)}
                              title="Elimina"
                              data-testid={`delete-doc-${doc._id}`}
                            >
                              <Trash2 size={14} />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Edit Document Modal */}
      {editingDoc && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg max-w-md w-full">
            <div className="border-b border-border px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-semibold">Modifica Documento</h2>
              <button onClick={() => setEditingDoc(null)} className="btn btn-secondary p-2">
                <X size={18} />
              </button>
            </div>
            <form onSubmit={handleEdit} className="p-6 space-y-4">
              <div className="form-group">
                <label className="form-label">Nome File</label>
                <input
                  type="text"
                  className="form-input"
                  value={editForm.nome_file}
                  onChange={(e) => setEditForm({...editForm, nome_file: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Descrizione</label>
                <textarea
                  className="form-input"
                  rows={3}
                  value={editForm.descrizione}
                  onChange={(e) => setEditForm({...editForm, descrizione: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Data Scadenza (opzionale)</label>
                <input
                  type="date"
                  className="form-input"
                  value={editForm.data_scadenza}
                  onChange={(e) => setEditForm({...editForm, data_scadenza: e.target.value})}
                />
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <button type="button" className="btn btn-secondary" onClick={() => setEditingDoc(null)}>
                  Annulla
                </button>
                <button type="submit" className="btn btn-primary">
                  Salva
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Documents;